import { t } from 'i18next';
import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { IContactGroupAddressListRequestPayload } from 'contacts/types';
import { SwitchInput } from 'core/components';
import { NON_EMPTY } from 'core/constants';
import { useFormContext } from 'core/states';
import { IAutocompleteOption, IAutocompleteRef } from 'core/types';
import { Size } from 'theme/enums';

import { ILocationFormFieldProps, LocationFormField } from './LocationFormField';

interface IProps extends ILocationFormFieldProps {
  requestPayload?: IContactGroupAddressListRequestPayload;
  isAlternateVersionEnabled?: boolean;
  disableArNumberFilterToggle?: boolean;
}

export const BillToLocationFormField = forwardRef(function BillToLocationFormField(
  {
    name = 'billToLocation',
    label = t('labelBillToLocation'),
    isAlternateVersionEnabled = false,
    disableArNumberFilterToggle = false,
    requestPayload: customRequestPayload,
    onCreateNewOption,
    ...props
  }: IProps,
  ref: ForwardedRef<IAutocompleteRef> | null
) {
  const [arNumberNotEmptyFilter, setArNumberNotEmptyFilter] =
    useState<boolean>(isAlternateVersionEnabled);

  const { defaultValues } = useFormContext();

  useEffect(() => {
    if (isAlternateVersionEnabled && defaultValues?.[name]) {
      setArNumberNotEmptyFilter(!!defaultValues[name].accountsReceivableId);
    }
  }, [defaultValues, isAlternateVersionEnabled, name]);

  const extraLabel = isAlternateVersionEnabled && (
    <SwitchInput
      size={Size.MD}
      label={t('labelARNumberOnly')}
      wrapperSx={{ marginBottom: 1 }}
      checked={arNumberNotEmptyFilter}
      disabled={disableArNumberFilterToggle}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setArNumberNotEmptyFilter(event.target.checked)
      }
    />
  );

  const handleCreateNewOption = useCallback(
    (location: IAutocompleteOption) => {
      if (isAlternateVersionEnabled && location) {
        setArNumberNotEmptyFilter(!!location.accountsReceivableId);
      }
      onCreateNewOption?.(location);
    },
    [isAlternateVersionEnabled, onCreateNewOption]
  );

  const requestPayload = useMemo(() => {
    return {
      ...customRequestPayload,
      filters: {
        ...customRequestPayload?.filters,
        ...(arNumberNotEmptyFilter && { accountsReceivableId: NON_EMPTY }),
        isBillTo: 1,
      },
    };
  }, [arNumberNotEmptyFilter, customRequestPayload]);

  return (
    <LocationFormField
      ref={ref}
      name={name}
      label={label}
      extraLabel={extraLabel}
      requestPayload={requestPayload}
      onCreateNewOption={handleCreateNewOption}
      {...props}
    />
  );
});
